import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

// ---- Constants ----

const MTP_SIMPLE_COLORS: Record<string, { bg: string; title: string; divider: string }> = {
  MDO: { bg: "#EAF3FB", title: "#1252A3", divider: "#C9DBEE" },
  MDE: { bg: "#ECF6EA", title: "#107C10", divider: "#C7DECC" },
  MDI: { bg: "#F3EEF8", title: "#5C2D91", divider: "#D9C8EF" },
  MDA: { bg: "#FFF4E5", title: "#C15A00", divider: "#E8D5B7" },
};

const MDC_SERVICE_CONFIG: Record<string, { bg: string; titleColor: string; icon: string }> = {
  Servers:    { bg: "#EAF3FB", titleColor: "#1252A3", icon: "ServerEnviroment" },
  DCSPM:      { bg: "#ECF6EA", titleColor: "#107C10", icon: "LockSolid" },
  Storage:    { bg: "#FFF4E5", titleColor: "#C15A00", icon: "Database" },
  Containers: { bg: "#FDE8E8", titleColor: "#A4262C", icon: "CubeShape" },
};

const SENTINEL_COMPONENT_CONFIG: Record<string, { bg: string; titleColor: string; divider: string }> = {
  "Continuous Ingestion": { bg: "#ECF6EA", titleColor: "#107C10", divider: "#C7DECC" },
  "5GB Sustained Usage":  { bg: "#ECF6EA", titleColor: "#107C10", divider: "#C7DECC" },
  "Ingestion Size":       { bg: "#EAF3FB", titleColor: "#1252A3", divider: "#C9DBEE" },
  "Groups Ratio":         { bg: "#FFF4E5", titleColor: "#C15A00", divider: "#E8D5B7" },
};

// ---- Helpers ----

const toText = (v: string | number): string => String(v ?? "").trim();

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
};

const formatInt = (v: number | undefined): string =>
  v === undefined ? "-" : v.toLocaleString();

const formatDecimal = (v: number | undefined, digits = 1): string =>
  v === undefined
    ? "-"
    : v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });

const formatCompactNumber = (v: number | undefined): string => {
  if (v === undefined) return "-";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${formatDecimal(v / 1_000_000_000, 1)}b`;
  if (abs >= 1_000_000)     return `${formatDecimal(v / 1_000_000, 1)}m`;
  if (abs >= 1_000)         return `${formatDecimal(v / 1_000, 1)}k`;
  return formatInt(v);
};

const getScoreColor = (score: number | undefined): string => {
  if (score === undefined) return "#605E5C";
  if (score >= 70) return "#107C10";
  if (score >= 40) return "#C15A00";
  return "#A4262C";
};

const isBinaryYes = (v: string | number): boolean =>
  ["1", "yes", "true"].includes(String(v).toLowerCase().trim());

// ---- Data Structures & Parsers ----

// Card 1: Simple (MDO / MDE / MDI / MDA)
interface SimpleData {
  score?: number;
  mpu?: number;
  pau?: number;
  deployRate?: number;
}

const buildSimpleData = (rows: RawIndicatorRow[]): SimpleData => {
  const result: SimpleData = {};
  for (const row of rows) {
    const label = (row.factorLabel || "").trim().toLowerCase();
    const v = parseNumber(row.value);
    if (label.includes("adjusted deployment rate") || label.includes("adjusted deployment")) {
      result.score = v;
    } else if (label.includes("monthly protected users") || label === "mpu") {
      result.mpu = v;
    } else if (label.includes("paid available units") || label === "pau") {
      result.pau = v;
    } else if (label.includes("deployment rate")) {
      result.deployRate = v;
    }
  }
  return result;
};

// Card 2: MDC
interface MDCServiceData {
  name: string;
  score?: number;
  protected?: number;
  total?: number;
}

interface MDCData {
  services: MDCServiceData[];
  compositeLines: Array<{ label: string; value: string | number }>;
}

const buildMDCData = (rows: RawIndicatorRow[]): MDCData => {
  const serviceMap: Record<string, MDCServiceData> = {
    Servers:    { name: "Servers" },
    DCSPM:      { name: "DCSPM" },
    Storage:    { name: "Storage" },
    Containers: { name: "Containers" },
  };
  const compositeLines: Array<{ label: string; value: string | number }> = [];

  for (const row of rows) {
    const section    = (row.sectionName || "").trim();
    const metricType = (row.metricName  || "").trim();
    const label      = (row.factorLabel || "").trim().toLowerCase();

    if (section === "Composite Calculation" && metricType === "Summary") {
      compositeLines.push({ label: row.factorLabel || toText(row.value), value: row.value });
      continue;
    }

    const service = serviceMap[section];
    if (!service || metricType !== "Factors") continue;

    if (label.includes("score") || label.includes("attach rate")) {
      service.score = parseNumber(row.value);
    } else if (label === "protected") {
      service.protected = parseNumber(row.value);
    } else if (label === "total") {
      service.total = parseNumber(row.value);
    }
  }

  return {
    services: [serviceMap.Servers, serviceMap.DCSPM, serviceMap.Storage, serviceMap.Containers],
    compositeLines,
  };
};

// Card 3: Sentinel
interface SentinelFactorEntry {
  label: string;
  value: string | number;
  isBinary: boolean;
  isFormula: boolean;
}

interface SentinelComponentData {
  name: string;
  scoreEarned?: number;
  scoreMax?: number;
  factors: SentinelFactorEntry[];
}

interface SentinelData {
  components: SentinelComponentData[];
  totalFormula?: string;
  totalScore?: number;
}

const SENTINEL_COMPONENT_ORDER = [
  "Continuous Ingestion",
  "5GB Sustained Usage",
  "Ingestion Size",
  "Groups Ratio",
];

const buildSentinelData = (rows: RawIndicatorRow[]): SentinelData => {
  const componentMap: Record<string, SentinelComponentData> = {};
  for (const name of SENTINEL_COMPONENT_ORDER) {
    componentMap[name] = { name, factors: [] };
  }

  let totalFormula: string | undefined;
  let totalScore: number | undefined;

  for (const row of rows) {
    const section    = (row.sectionName || "").trim();
    const metricType = (row.metricName  || "").trim();
    const label      = (row.factorLabel || "").trim();

    if (section === "Total Sentinel Deployment Score" && metricType === "Summary") {
      totalFormula = label;
      totalScore   = parseNumber(row.value);
      continue;
    }

    const comp = componentMap[section];
    if (!comp || metricType !== "Factors") continue;

    // Score Earned row: "Score Earned (Max 35): 35.0"
    const scoreEarnedMatch = label.match(/Score Earned \(Max (\d+(?:\.\d+)?)\)/i);
    if (scoreEarnedMatch) {
      comp.scoreMax    = parseNumber(scoreEarnedMatch[1]);
      comp.scoreEarned = parseNumber(row.value);
      continue;
    }

    const isFormula = label.toLowerCase().startsWith("formula:");
    const isBinary  = label.toLowerCase().includes("(1=yes") ||
                      label.toLowerCase().includes("(binary:");

    comp.factors.push({ label, value: row.value, isBinary, isFormula });
  }

  return {
    components: SENTINEL_COMPONENT_ORDER.map((n) => componentMap[n]),
    totalFormula,
    totalScore,
  };
};

// ---- Shared Sub-components ----

const SummaryRow = ({
  score, weight, contribution, scoreColor,
}: {
  score: string; weight: string; contribution: string; scoreColor: string;
}) => (
  <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%" } }}>
    {[
      { label: "Score",        value: score,        color: scoreColor  },
      { label: "Weight",       value: weight,       color: undefined   },
      { label: "Contribution", value: contribution, color: undefined   },
    ].map(({ label, value, color }) => (
      <Stack key={label} styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>{label}</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: color || "inherit" } }}>
          {value}
        </Text>
      </Stack>
    ))}
  </Stack>
);

// ---- Card 1: Simple Deployment Card (MDO / MDE / MDI / MDA) ----

const SimpleDeploymentCard = ({
  data, productAbbr,
}: {
  data: SimpleData; productAbbr: string;
}) => {
  const colors   = MTP_SIMPLE_COLORS[productAbbr] ?? { bg: "#F5F5F5", title: "#323130", divider: "#D9D9D9" };
  const score    = data.score;
  const scoreText      = score !== undefined ? formatDecimal(score, 1) : "-";
  const contribution   = score !== undefined ? `${formatDecimal(score, 1)} pts` : "-";

  const factors = [
    { label: "MPU",         value: formatCompactNumber(data.mpu) },
    { label: "PAU",         value: formatCompactNumber(data.pau) },
    {
      label: "Deploy Rate",
      value: data.deployRate !== undefined ? `${formatDecimal(data.deployRate, 1)}%` : "-",
    },
  ];

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          width: 360,
          minWidth: 320,
          borderRadius: 8,
          padding: 16,
          border: "1px solid #D9D9D9",
          backgroundColor: colors.bg,
        },
      }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: colors.title } }}>
        {productAbbr} — Deployment Score
      </Text>

      <SummaryRow
        score={scoreText}
        weight="100%"
        contribution={contribution}
        scoreColor={colors.title}
      />

      <div style={{ height: 1, backgroundColor: colors.divider }} />

      <Text variant="medium" styles={{ root: { fontWeight: 600, color: colors.title } }}>
        Factors
      </Text>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        {factors.map((factor) => (
          <Stack
            key={factor.label}
            styles={{
              root: {
                minHeight: 86,
                border: "1px solid #D9D9D9",
                borderRadius: 6,
                backgroundColor: "#FFFFFF",
                padding: "8px 10px",
                alignItems: "center",
                justifyContent: "space-between",
              },
            }}
          >
            <Text variant="small" styles={{ root: { textAlign: "center", color: "#605E5C" } }}>
              {factor.label}
            </Text>
            <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>
              {factor.value}
            </Text>
          </Stack>
        ))}
      </div>
    </Stack>
  );
};

// ---- Card 2: MDC Composite Card ----

const MDCCard = ({ data }: { data: MDCData }) => (
  <Stack
    tokens={{ childrenGap: 16 }}
    styles={{
      root: {
        borderRadius: 8,
        padding: 20,
        border: "1px solid #D9D9D9",
        backgroundColor: "#F8F9FA",
        maxWidth: "100%",
      },
    }}
  >
    {/* Header */}
    <Stack tokens={{ childrenGap: 4 }}>
      <Text variant="large" styles={{ root: { fontWeight: 700, color: "#1252A3" } }}>
        Deployment Sub-Indicators — MDC (Defender for Cloud)
      </Text>
      <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
        MDC composite score is built from 4 service attach rates plus a scale bonus:
      </Text>
    </Stack>

    {/* Service cards */}
    <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
      {data.services.map((service) => {
        const config     = MDC_SERVICE_CONFIG[service.name] ?? { bg: "#F5F5F5", titleColor: "#323130", icon: "Work" };
        const scoreColor = getScoreColor(service.score);

        return (
          <Stack
            key={service.name}
            tokens={{ childrenGap: 10 }}
            styles={{
              root: {
                flex: "1 1 180px",
                minWidth: 160,
                borderRadius: 8,
                padding: 14,
                border: "1px solid #D9D9D9",
                backgroundColor: config.bg,
              },
            }}
          >
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <Icon iconName={config.icon} styles={{ root: { color: config.titleColor, fontSize: 16 } }} />
              <Text variant="medium" styles={{ root: { fontWeight: 700, color: config.titleColor } }}>
                {service.name}
              </Text>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Stack styles={{ root: { flex: 1 } }}>
                <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Score</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: scoreColor } }}>
                  {service.score !== undefined ? formatDecimal(service.score, 1) : "-"}
                </Text>
              </Stack>
              <Stack styles={{ root: { flex: 1 } }}>
                <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Protected</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>
                  {formatInt(service.protected)}
                </Text>
              </Stack>
              <Stack styles={{ root: { flex: 1 } }}>
                <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Total</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>
                  {formatInt(service.total)}
                </Text>
              </Stack>
            </Stack>
          </Stack>
        );
      })}
    </Stack>

    {/* Composite Calculation */}
    {data.compositeLines.length > 0 && (
      <Stack
        tokens={{ childrenGap: 6 }}
        styles={{
          root: {
            backgroundColor: "#EFF6FF",
            border: "1px solid #C7DEFA",
            borderRadius: 8,
            padding: 16,
          },
        }}
      >
        <Text variant="medium" styles={{ root: { fontWeight: 700, color: "#1252A3" } }}>
          Composite Calculation:
        </Text>
        {data.compositeLines.map((line, idx) => {
          const isFinal = line.label.toLowerCase().includes("final");
          return (
            <Text
              key={idx}
              variant="small"
              styles={{ root: { fontWeight: isFinal ? 700 : 400 } }}
            >
              {line.label}
            </Text>
          );
        })}
      </Stack>
    )}
  </Stack>
);

// ---- Card 3: Sentinel Card ----

const SentinelCard = ({ data }: { data: SentinelData }) => (
  <Stack
    tokens={{ childrenGap: 16 }}
    styles={{
      root: {
        borderRadius: 8,
        padding: 20,
        border: "1px solid #D9D9D9",
        backgroundColor: "#F8F9FA",
        maxWidth: "100%",
      },
    }}
  >
    {/* Header */}
    <Stack tokens={{ childrenGap: 4 }}>
      <Text variant="large" styles={{ root: { fontWeight: 700, color: "#1252A3" } }}>
        Deployment Sub-Indicators — Sentinel
      </Text>
      <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
        Sentinel score is additive across 4 components (max 100 points total):
      </Text>
    </Stack>

    {/* Component cards */}
    <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
      {data.components.map((comp) => {
        const config = SENTINEL_COMPONENT_CONFIG[comp.name] ?? { bg: "#F5F5F5", titleColor: "#323130", divider: "#D9D9D9" };
        const scoreEarnedColor =
          comp.scoreEarned === undefined ? "#605E5C" :
          comp.scoreEarned === 0         ? "#A4262C" :
          comp.scoreEarned >= (comp.scoreMax ?? 0) ? config.titleColor :
          "#C15A00";

        return (
          <Stack
            key={comp.name}
            tokens={{ childrenGap: 12 }}
            styles={{
              root: {
                flex: "1 1 220px",
                minWidth: 200,
                borderRadius: 8,
                padding: 14,
                border: "1px solid #D9D9D9",
                backgroundColor: config.bg,
              },
            }}
          >
            {/* Title */}
            <Text variant="medium" styles={{ root: { fontWeight: 700, color: config.titleColor } }}>
              {comp.name}
            </Text>

            {/* Score Earned / Score Max */}
            <Stack horizontal tokens={{ childrenGap: 24 }}>
              <Stack>
                <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Score Earned</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: scoreEarnedColor } }}>
                  {comp.scoreEarned !== undefined ? formatDecimal(comp.scoreEarned, 1) : "-"}
                </Text>
              </Stack>
              <Stack>
                <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Score Max</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>
                  {comp.scoreMax !== undefined ? String(comp.scoreMax) : "-"}
                </Text>
              </Stack>
            </Stack>

            <div style={{ height: 1, backgroundColor: config.divider }} />

            <Text variant="small" styles={{ root: { fontWeight: 600, color: config.titleColor } }}>
              How score is calculated
            </Text>

            {/* Factor rows */}
            <Stack tokens={{ childrenGap: 6 }}>
              {comp.factors.map((factor, idx) => {
                if (factor.isFormula) {
                  return (
                    <Text
                      key={idx}
                      variant="small"
                      styles={{ root: { fontStyle: "italic", color: "#323130" } }}
                    >
                      {factor.label}
                    </Text>
                  );
                }

                if (factor.isBinary) {
                  const yes = isBinaryYes(factor.value);
                  return (
                    <Stack
                      key={idx}
                      horizontal
                      horizontalAlign="space-between"
                      verticalAlign="center"
                      tokens={{ childrenGap: 8 }}
                    >
                      <Text variant="small" styles={{ root: { color: "#605E5C", flex: 1 } }}>
                        {factor.label}
                      </Text>
                      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        <Icon
                          iconName={yes ? "CheckMark" : "Cancel"}
                          styles={{ root: { color: yes ? "#107C10" : "#A4262C", fontSize: 14, fontWeight: 700 } }}
                        />
                        <Text
                          variant="small"
                          styles={{ root: { fontWeight: 700, color: yes ? "#107C10" : "#A4262C" } }}
                        >
                          {yes ? "Yes" : "No"}
                        </Text>
                      </Stack>
                    </Stack>
                  );
                }

                const numericVal = parseNumber(factor.value);
                const displayVal = numericVal !== undefined
                  ? formatDecimal(numericVal, 2)
                  : toText(factor.value);

                return (
                  <Stack
                    key={idx}
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                  >
                    <Text variant="small" styles={{ root: { color: "#605E5C", flex: 1 } }}>
                      {factor.label}
                    </Text>
                    <Text variant="small" styles={{ root: { fontWeight: 700 } }}>
                      {displayVal}
                    </Text>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        );
      })}
    </Stack>

    {/* Total Score Footer */}
    {data.totalFormula !== undefined && (
      <Stack
        horizontalAlign="center"
        styles={{
          root: {
            backgroundColor: "#EFF6FF",
            border: "1px solid #C7DEFA",
            borderRadius: 8,
            padding: "12px 20px",
          },
        }}
      >
        <Text
          variant="large"
          styles={{ root: { fontWeight: 700, color: "#1252A3", textAlign: "center" } }}
        >
          Total Sentinel Deployment Score = {data.totalFormula}
        </Text>
      </Stack>
    )}
  </Stack>
);

// ---- Main Component ----

interface MTPDeploymentRawIndicatorCardsProps {
  rows: RawIndicatorRow[];
  productAbbr: string;
}

const MTPDeploymentRawIndicatorCards: React.FC<MTPDeploymentRawIndicatorCardsProps> = ({
  rows,
  productAbbr,
}) => {
  const mdcData = useMemo(
    () => (productAbbr === "MDC" ? buildMDCData(rows) : null),
    [rows, productAbbr]
  );
  const sentinelData = useMemo(
    () => (productAbbr === "Sentinel" ? buildSentinelData(rows) : null),
    [rows, productAbbr]
  );
  const simpleData = useMemo(
    () => (productAbbr !== "MDC" && productAbbr !== "Sentinel" ? buildSimpleData(rows) : null),
    [rows, productAbbr]
  );

  if (productAbbr === "MDC" && mdcData)       return <MDCCard data={mdcData} />;
  if (productAbbr === "Sentinel" && sentinelData) return <SentinelCard data={sentinelData} />;
  if (simpleData)                              return <SimpleDeploymentCard data={simpleData} productAbbr={productAbbr} />;
  return null;
};

export default MTPDeploymentRawIndicatorCards;
